import type { ApplicationStatus, ProgressStatus } from '../types/application'
import type { PropertyTabInlineField } from '../utils/propertyTabValidator'

export {
  APPLICATION_CHECKLIST_SIDEBAR_WIDTH_PX,
  APPLICATION_CHECKLIST_TAB_STRIP_GAP_PX,
  APPLICATION_CHECKLIST_TAB_SCROLL_BELOW_PX,
} from './checklistLayout'

export { RESIDENT_APPLICATION_ENRICHMENT_CONFIG } from './residentApplicationRequest'

// ─── Application detail tabs ─────────────────────────────────────────────────

export const ALL_APPLICATION_TABS = [
  {
    id: 'aboutyourselfinfo' as const,
    label: 'About Yourself',
    policy: 'application_detail_about_yourself_view',
  },
  {
    id: 'propertyinfo' as const,
    label: 'Property',
    policy: 'application_detail_property_view',
  },
  {
    id: 'employmentinfo' as const,
    label: 'Income and Employment',
    policy: 'application_detail_income_employment_view',
  },
  {
    id: 'questioninfo' as const,
    label: 'Personal History',
    policy: 'application_detail_personal_history_view',
  },
  {
    id: 'signatureinfo' as const,
    label: 'Signature/Payment',
    policy: 'application_detail_signature_payment_view',
  },
  {
    id: 'notesinfo' as const,
    label: 'Notes',
    policy: 'application_detail_notes_view',
  },
  {
    id: 'creditinfo' as const,
    label: 'Credit Report',
    policy: 'application_detail_credit_report_view',
  },
] as const

// ─── Application list columns ────────────────────────────────────────────────

export const APPLICATION_LIST_COLUMN_OPTIONS = [
  { id: 'applicant_id', label: 'Applicant' },
  { id: 'application_id', label: 'Application' },
  { id: 'property_name', label: 'Property' },
  { id: 'product_name', label: 'Product' },
  { id: 'model_name', label: 'Model' },
  { id: 'application_risk_rating', label: 'Application Risk Rating' },
  { id: 'applicant_risk_rating', label: 'Applicant Risk Rating' },
  { id: 'application_screening_approach', label: 'Application Screening Approach' },
  { id: 'applicant_screening_approach', label: 'Applicant Screening Approach' },
  { id: 'resident_name', label: 'Resident Name' },
  { id: 'email', label: 'Email' },
  { id: 'userwriter_name', label: 'Underwriter' },
  { id: 'invite_code', label: 'Invite Code' },
  { id: 'mobile_no', label: 'Phone Number' },
  { id: 'co_app', label: 'Co-App' },
  { id: 'payment_due', label: 'Payment Due' },
  { id: 'underwriting_sort', label: 'Last Updated' },
  { id: 'applicant_status', label: 'Status' },
  { id: 'progress_status', label: 'Progress' },
  { id: 'past_customer', label: 'Past Customer' },
  { id: 'corelogic_applicant_score', label: 'Credit Score' },
  { id: 'income_score', label: 'Income Score' },
  { id: 'certificate_paid', label: 'Contract Paid' },
  { id: 'impersonate', label: 'Impersonate' },
] as const

/** Always rendered in the list table; excluded from Customize columns. */
export const APPLICATION_LIST_LOCKED_COLUMN_IDS = ['applicant_id'] as const

export const APPLICATION_LIST_CUSTOMIZABLE_COLUMN_OPTIONS =
  APPLICATION_LIST_COLUMN_OPTIONS.filter(
    (column) => column.id !== 'applicant_id' && column.id !== 'impersonate',
  )

/** All data columns (excludes impersonate action column). */
export const APPLICATION_LIST_DATA_COLUMN_IDS = APPLICATION_LIST_COLUMN_OPTIONS.map(
  (column) => column.id,
).filter(
  (
    column,
  ): column is Exclude<
    (typeof APPLICATION_LIST_COLUMN_OPTIONS)[number]['id'],
    'impersonate'
  > => column !== 'impersonate',
)

export const APPLICATION_COLUMN_VISIBILITY_STORAGE_KEY = 'application-list-column-visibility'

/** Invite List / Property List loading spinner styling. */
export const APPLICATION_MODULE_SPINNER_CLASS = 'animate-spin text-[rgb(21_151_255)]'

export const APPLICATION_MODULE_LIST_SPINNER_CLASS = `h-8 w-8 ${APPLICATION_MODULE_SPINNER_CLASS}`

// ─── Application list filters & roles ────────────────────────────────────────

/** Matches Angular `user-access.ts` `liberty_roles` — used for list/grid columns (e.g. Underwriter). */
export const LIBERTY_ROLES_FOR_APPLICATION_LIST: string[] = [
  'ROLE_ADMIN',
  'ROLE_EXECS',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_LIBERTY_SALES',
  'ROLE_UNDERWRITER',
  'ROLE_LIBERTY_FIN_MGR',
  'EXTERNAL_INSURANCE',
  'ROLE_LIBERTY_ACCOUNTING',
  'ROLE_REFUND_APPROVAL',
]

/** Angular application-details Update Ninja/Equifax Score — Admin, Underwriter, Liberty Accounting. */
export const NINJA_EQUIFAX_SCORE_UPDATE_ROLES: readonly string[] = [
  'ROLE_ADMIN',
  'ROLE_UNDERWRITER',
  'ROLE_LIBERTY_ACCOUNTING',
]

/** Hostnames allowed to show Update Ninja/Equifax Score (dev/stage only). */
export const NINJA_EQUIFAX_SCORE_UPDATE_HOSTNAMES: readonly string[] = [
  'localhost',
  '127.0.0.1',
  '[::1]',
  'v2-dev.libertyrent.com',
  'v2-cornerstage.libertyrent.com',
]

/** True when the app is running on a host allowed to show Update Ninja/Equifax Score. */
export function isNinjaEquifaxScoreUpdateHost(hostname?: string): boolean {
  const host = (
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  )
    .trim()
    .toLowerCase()
  return NINJA_EQUIFAX_SCORE_UPDATE_HOSTNAMES.includes(host)
}

/** Manual CoreLogic score edit — ADMIN + UNDERWRITER only (narrower than Ninja/Equifax refresh). */
export const CORELOGIC_SCORE_EDIT_ROLES: readonly string[] = [
  'ROLE_ADMIN',
  'ROLE_UNDERWRITER',
]

/** Angular cancelApplicationPopup AllowdRole — others cancel immediately without notify dialog. */
export const CANCEL_APPLICATION_NOTIFY_DIALOG_ROLES: readonly string[] = [
  'ROLE_ADMIN',
  'ROLE_UNDERWRITER',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_LIBERTY_ACCOUNTING',
]

/** Application status options for filter dropdown */
export const APPLICATION_STATUS_OPTIONS: Array<{ value: ApplicationStatus; label: string }> = [
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PROPERTY_APPROVED', label: 'Property Approved' },
  { value: 'DENIED', label: 'Denied' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'AWAITING', label: 'Awaiting' },
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'CANCELLED', label: 'Cancelled' },
]

/** Progress options per applicant status — Angular application.component.ts statusChange(). */
export const PROGRESS_STATUS_MAP: Record<ApplicationStatus, ProgressStatus[]> = {
  NOT_STARTED: ['REGISTERED'],
  INCOMPLETE: [
    'APPLICATION_STARTED',
    'APPLICATION_INCOMPLETE',
    'APPLICATION_COMPLETE',
    'APPLICATION_PAID',
  ],
  PENDING: [
    'CREDIT_REPORT_PENDING',
    'CREDIT_REPORT_READY',
    'CREDIT_REPORT_FAILED',
    'UNDERWRITING_STARTED',
    'UNDERWRITING_WAITING_EXTERNAL',
    'UNDERWRITING_IN_PROGRESS',
    'UNDERWRITING_READY',
    'UNDERWRITING_SPLIT',
  ],
  APPROVED: [
    'APPROVED',
    'CERTIFICATE_NOT_PAID',
    'CERTIFICATE_PAID',
    'CERTIFICATE_ADJUSTMENT',
    'CERTIFICATE_SENT',
    'LEASE_INITIATED',
    'RESUBMIT_CREDIT_REPORT',
  ],
  AWAITING: ['AWAITING_EXTERNAL', 'IN_PROGRESS'],
  DENIED: ['DENIED'],
  EXPIRED: ['EXPIRED'],
  CANCELLED: ['CANCELLED'],
  PROPERTY_APPROVED: [],
}

/** Progress status labels */
export const PROGRESS_STATUS_LABELS: Record<ProgressStatus, string> = {
  REGISTERED: 'User Registered',
  APPLICATION_STARTED: 'Application Started',
  APPLICATION_INCOMPLETE: 'Application Incomplete',
  APPLICATION_COMPLETE: 'Application Complete',
  APPLICATION_PAID: 'Application Paid',
  CREDIT_REPORT_PENDING: 'Credit Report Pending',
  CREDIT_REPORT_READY: 'Credit Report Ready',
  CREDIT_REPORT_FAILED: 'Credit Report Failed',
  UNDERWRITING_STARTED: 'Screening Started',
  UNDERWRITING_WAITING_EXTERNAL: 'Screening Waiting External',
  UNDERWRITING_IN_PROGRESS: 'Screening In Process',
  UNDERWRITING_READY: 'Screening Ready',
  UNDERWRITING_SPLIT: 'Screening Split',
  APPROVED: 'Screening Approved',
  CERTIFICATE_NOT_PAID: 'Approved-Cert not Paid',
  DENIED: 'Screening Denied',
  AWAITING_EXTERNAL: 'Awaiting External',
  IN_PROGRESS: 'In Progress',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  CERTIFICATE_PAID: 'Access Paid',
  POLICY_ACTIVATION_PENDING: 'Policy Activation Pending',
  CERTIFICATE_ADJUSTMENT: 'Access Adjustment',
  CERTIFICATE_SENT: 'Access Sent',
  LEASE_INITIATED: 'Lease Initiated',
  RESUBMIT_CREDIT_REPORT: 'Resubmit Credit Report',
  ACTIVATION_COMPLETE: 'Activation Complete',
}

/** Angular Continue-button guard: progress_status === 'Screening In Process'. */
export function isScreeningInProcessProgressStatus(progressStatus: unknown): boolean {
  if (progressStatus == null) return false
  return String(progressStatus).trim() === 'Screening In Process'
}

const PROGRESS_STATUS_ORDER: ProgressStatus[] = Object.values(PROGRESS_STATUS_MAP).flat()

/** Get progress status options based on selected applicant statuses. */
export function getProgressStatusOptions(
  applicantStatuses: ApplicationStatus[],
): Array<{ value: ProgressStatus; label: string }> {
  const progressStatuses = new Set<ProgressStatus>()

  applicantStatuses.forEach((status) => {
    const progressList = PROGRESS_STATUS_MAP[status] || []
    progressList.forEach((ps) => progressStatuses.add(ps))
  })

  return Array.from(progressStatuses)
    .sort((a, b) => {
      const indexA = PROGRESS_STATUS_ORDER.indexOf(a)
      const indexB = PROGRESS_STATUS_ORDER.indexOf(b)
      return indexA - indexB
    })
    .map((ps) => ({
      value: ps,
      label: PROGRESS_STATUS_LABELS[ps],
    }))
}

/** Sortable columns for application list */
export const SORTABLE_COLUMNS = [
  'applicant_id',
  'application_id',
  'property_name',
  'resident_name',
  'email',
  'mobile_no',
  'underwriting_sort',
  'applicant_status',
  'progress_status',
  'past_customer',
  'userwriter_name',
  'corelogic_applicant_score',
  'income_score',
  'certificate_paid',
  'application_risk_rating',
  'applicant_risk_rating',
  'application_screening_approach',
  'applicant_screening_approach',
] as const

/** Per page options for pagination */
export const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

/** Employer search options */
export const EMPLOYER_SEARCH_OPTIONS = [
  { value: 'employment', label: 'Employers' },
  { value: 'other_income', label: 'Other Income' },
  { value: 'job_file', label: 'Proof of Income File' },
] as const

/** Past customer options */
export const PAST_CUSTOMER_OPTIONS = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
] as const

// ─── Application detail page ─────────────────────────────────────────────────

export const LEASE_DETAILS_EMAIL_PROGRESS_STATUSES = new Set([
  'CERTIFICATE_PAID',
  'Access Paid',
  'CERTIFICATE_SENT',
  'Access Sent',
])

export const STATUS_MAP: Record<string, string> = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PROPERTY_APPROVED: 'Property Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  VOID: 'Void',
  AWAITING: 'Awaiting',
  CANCELLED: 'Cancelled',
  NOT_STARTED: 'Not Started',
  UNDERWRITING_WAITING_EXTERNAL: 'Suspend',
  UNDERWRITING_IN_PROGRESS: 'Continue',
  REFUND_PROPERTY: 'Refund Property',
  REFUND_APPLICANT: 'Refund Applicant',
  CHARGEBACK_NON_LEASED: 'Chargeback Nonlease',
  CHARGEBACK_PAYMENT_NOT_AUTHORIZED: 'Chargeback - Payment Not Authorized',
  CHARGEBACK_BANK_HOLD: 'Chargeback - Bank Hold',
  UNDERWRITING_STARTED: 'Screening Started',
}

export const CO_RESIDENT_RELATIONSHIP_TYPES: Record<string, string> = {
  SPOUSE: 'Spouse/Partner',
  SIBLING: 'Sibling',
  CHILD: 'Child',
  PARENT: 'Parent',
  NOT_DIRECTLY_RELATED: 'Not Directly Related',
  CO_SIGNER: 'Co-signer',
}

// ─── About Yourself tab ──────────────────────────────────────────────────────

export const EMAIL_VALIDATE_ROLES = [
  'ROLE_UNDERWRITER',
  'ROLE_ADMIN',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_REFUND_APPROVAL',
]

/** Angular `application-details` address template: View on map only for these roles */
export const ADDRESS_MAP_LINK_ROLES: readonly string[] = ['ROLE_ADMIN', 'ROLE_UNDERWRITER']

export const ADDRESS_TYPES: Record<string, string> = {
  RENT: 'Rent',
  OWN: 'Own',
  FAMILY: 'Family or Other',
}

export const COMMUNICATION_PREFERENCES = ['Text Msg', 'Email']

/** Relationship types for FAMILY address type (Contact Relationship dropdown) */
export const ABOUT_YOURSELF_RELATIONSHIP_TYPES: Record<string, string> = {
  PARENT_CHILD: 'Parent/Child',
  RELATIVE: 'Relative',
  FRIEND: 'Friend',
}

export const US_STATE_OPTIONS = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
] as const

export const ABOUT_YOURSELF_REVEAL_INLINE_ERRORS_EVENT =
  'liberty:about-yourself:reveal-inline-errors'

// ─── Property tab ────────────────────────────────────────────────────────────

export const PROPERTY_INLINE_FIELDS = new Set<PropertyTabInlineField | string>([
  'property_name',
  'rent_amount',
  'lease_start_date',
  'rent_lease_reason',
  'unit_number',
  'co_residents',
  'agent_name',
  'agent_email',
  'agent_phone',
])

/** Angular resident-application-add `alert-ylw` copy, shown verbatim. */
export const PROPERTY_NOT_WORKING_ALERT_MESSAGE =
  'This property selected is not currently working with Liberty Rent. Please bear with us while we contact the property. Do you want to continue completing your application or save and exit?'

export const EDIT_LEASE_DURATION_MONTHS = 12

export const RENT_LEASE_REASONS = [
  { value: '', label: 'Select Reason' },
  {
    value: 'Rent Changed - Prior to Occupancy',
    label: 'Rent Changed - Prior to Occupancy',
  },
  {
    value: 'Rent Changed - Transfer within Complex',
    label: 'Rent Changed - Transfer within Complex',
  },
  { value: 'Lease Start Moved', label: 'Lease Start Moved' },
]

/** Coded relationship enum — backend `relationship_type` expects these codes. */
export const RELATIONSHIP_OPTIONS = [
  { value: 'SPOUSE', label: 'Spouse/Partner' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'CHILD', label: 'Child' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'NOT_DIRECTLY_RELATED', label: 'Not Directly Related' },
  { value: 'CO_SIGNER', label: 'Co-signer' },
]

// ─── Income & Employment tab ─────────────────────────────────────────────────

/** Angular `appAmount` allows up to ~22 digits before the decimal across most amount fields. */
export const INCOME_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL = 22

export const VERIFIED_INCOME_ROLES = ['ROLE_ADMIN', 'ROLE_UNDERWRITER']

/** Policy for Reset Bank Link on Income and Employment tab. */
export const APPLICATION_BANK_LINK_RESET_POLICY = 'application_bank_link_reset'

/** Angular `bankLinkingLogsViewerRoles` — who can open Bank linking logs (View Logs). */
export const BANK_LINKING_LOGS_VIEWER_ROLES: readonly string[] = [
  'ROLE_ADMIN',
  'ROLE_UNDERWRITER',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_EXECS',
]

export const EMPLOYMENT_TYPES: Record<string, string> = {
  FULL_TIME: 'Full Time (more than 32 hrs/ week)',
  PART_TIME: 'Part Time',
  SELF_EMPLOYED: 'Self-Employed',
}

export const EMPLOYMENT_TYPES_VIEW: Record<string, string> = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  SELF_EMPLOYED: 'Self-Employed',
}

export const INCOME_PERIODS: Record<string, string> = {
  Per_Month: 'Monthly',
  Per_Week: 'Weekly',
  Per_Bi_Monthly: 'Bi-Monthly',
  Per_Year: 'Yearly',
  One_Time: 'One Time',
}

export const CHECK_TYPES: Record<string, string> = {
  PAYSTUBS: 'Paystubs',
  BANK_STATEMENTS: 'Bank Statements',
  BENEFIT_LETTERS: 'Benefit Letters',
  JOB_OFFER: 'Job offer',
}

export const OTHER_INCOME_PERIODS: Record<string, string> = {
  Per_Month: 'Monthly',
  One_Time: 'One Time',
}

// ─── Notes tab ───────────────────────────────────────────────────────────────

/** Angular `note-list.loadNotes`: default User Notes filter for these roles on APPLICATION. */
export const APPLICATION_DEFAULT_USER_NOTE_FILTER_ROLES: readonly string[] = [
  'ROLE_UNDERWRITER',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
]

export const ADDITIONAL_FILES_RECEIVED_TYPE = 'ADDITIONAL_FILES_RECEIVED'
export const NOTE_ATTACHMENT_FILE_TYPE = 'ADDITIONAL_ATTACHMENTS'
export const NOTE_ATTACHMENT_MODULE = 'NOTES'

// ─── Signature / Payment tab ─────────────────────────────────────────────────

export const NAME_OVERRIDE_REASON_OPTIONS = [
  'Legally changed name',
  'Unmarried name',
  'Trust / Executor Name',
  'Power of Attorney',
] as const

// ─── Payment adjustment modal ──────────────────────────────────────────────────

export const ACCESS_FEE_REASONS = [
  'Background Failed at Property',
  'Miscommunication of Fees - Property at Fault',
  'Customer Backed Out',
  'Job Transfer/Military',
  'Property Issue',
  'Roommate Backed Out',
  'Family Emergency',
  'Injury/Health/Death',
  'Loss of Job/Income',
  'Constructive Eviction',
  'Other',
]

export const APPLICATION_FEE_INVOICE_REASONS: Array<[string, string]> = [
  ['CHANGE_PAYMENT', 'Change Payment / Re-Invoice'],
]

export const CONTRACT_INVOICE_REASONS: Array<[string, string]> = [
  ['RENT_INCREASED', 'Rent Increased'],
  ['CHANGE_PAYMENT', 'Change Payment / Re-Invoice'],
]

export const CHARGEBACK_CONFIRM_MESSAGE =
  "Is this for a 'Chargeback Dispute' or 'Permissible Use to pull Credit Report'?"
